import {
  Anchor,
  Box,
  Button,
  Center,
  Checkbox,
  Group,
  Loader,
  Menu,
  SegmentedControl,
  Stack,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { IProgressData, IValueLabel } from "@mctypes/global.types";
import { IconChevronDown, IconDatabaseImport, IconFilter2Search, IconStar, IconStarFilled } from "@tabler/icons-react";
import { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { useCallback, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { usePostIssueboardFavoriteToggleMutation } from "../../../../openapi";
import { AG_GRID_LOCALE_NOR } from "../../../agGridLocale";
import { apiSlice } from "../../../apiSlice";
import { CreateNewButton } from "../../../components";
import { AccessView, generateAccessText } from "../../../components/AccessComp/AccessComp";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { formatShortDate } from "../admin/selectors";
import { useGetIssueBoardsQuery } from "./IssueBoardRTK";
import IssueSearch from "./IssueSearch";
import { ISSUE_BOARD_STATUSES, IssueBoardStatusEnums } from "./issueConstants";
import {
  selectIssueBoardActiveStatusSelection,
  selectIssueBoardsTableActiveStatus,
  selectIssueBoardStatusCounts,
  selectIssueBoardTablePnsItems,
  selectIssueBoardTableRows,
  selectSelectedMultiRegisterIssueBoardIds,
} from "./MultiRegisterIssues/multiRegisterIssuesSelectors";
import {
  setIssueBoardsTableActiveStatusInRedux,
  setSelectedMultiRegisterIssueBoardIdsInRedux,
  toggleSelectedMultiRegisterIssueBoardIdInRedux,
} from "./MultiRegisterIssues/multiRegisterIssuesSlice";

const IssueBoardsTable: React.FC<{
  addNew: () => void;
}> = ({ addNew }) => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const project = useAppSelector((state) => state.adminReducer.project);
  const activeStatus = useAppSelector(selectIssueBoardsTableActiveStatus);
  const filteredBoards = useAppSelector(selectIssueBoardTableRows);
  const statusCounts = useAppSelector(selectIssueBoardStatusCounts);
  const pnsItems = useAppSelector(selectIssueBoardTablePnsItems);
  const selectedMultiRegisterIssueBoardIds = useAppSelector(selectSelectedMultiRegisterIssueBoardIds);
  const selectedMultiRegisterIssueBoardCount = selectedMultiRegisterIssueBoardIds.length;
  const { activeStatusBoardIds, allActiveStatusBoardsSelected, someActiveStatusBoardsSelected } = useAppSelector(
    selectIssueBoardActiveStatusSelection,
  );
  const [filterText, setFilterText] = useState<string>("");
  const [toggleFavorite] = usePostIssueboardFavoriteToggleMutation();
  const { isLoading } = useGetIssueBoardsQuery(
    { projectId: project?._id },
    {
      skip: project?._id == null,
    },
  );

  // Toggle favoritt
  const handleToggleFavorite = useCallback(
    (issueboardId: string) => (e: React.MouseEvent) => {
      e.stopPropagation();
      toggleFavorite({
        body: {
          projectId: project._id,
          issueboardId,
        },
      });
    },
    [project._id, toggleFavorite],
  );

  const goToBoard = useCallback(
    async (board: { _id: string }) => {
      dispatch(apiSlice.util.invalidateTags(["IssueBoard"]));
      history.push(`/project/${project._id}/issues/${board._id}`);
    },
    [dispatch, history, project._id],
  );

  // Klikk på tittel
  const handleTitleClick = useCallback(
    (board: { _id: string }) => (e: React.MouseEvent) => {
      e.stopPropagation();
      goToBoard(board);
    },
    [goToBoard],
  );

  const readDataFromSelectedBoards = useCallback(() => {
    if (selectedMultiRegisterIssueBoardCount === 0) return;
    const searchParams = new URLSearchParams({
      issueboardIds: selectedMultiRegisterIssueBoardIds.join(","),
    });
    history.push(`/project/${project._id}/issues-multiregister?${searchParams.toString()}`);
  }, [history, project._id, selectedMultiRegisterIssueBoardCount, selectedMultiRegisterIssueBoardIds]);

  const handleToggleAllActiveStatusBoards = useCallback(() => {
    const activeStatusBoardIdsSet = new Set(activeStatusBoardIds);
    if (allActiveStatusBoardsSelected) {
      dispatch(
        setSelectedMultiRegisterIssueBoardIdsInRedux(
          selectedMultiRegisterIssueBoardIds.filter((boardId) => !activeStatusBoardIdsSet.has(boardId)),
        ),
      );
      return;
    }

    dispatch(
      setSelectedMultiRegisterIssueBoardIdsInRedux(
        Array.from(new Set([...selectedMultiRegisterIssueBoardIds, ...activeStatusBoardIds])),
      ),
    );
  }, [activeStatusBoardIds, allActiveStatusBoardsSelected, dispatch, selectedMultiRegisterIssueBoardIds]);

  // Kolonnedefinisjoner (memoized)
  const issueBoardColumnDefs: ColDef<any>[] = useMemo(
    () => [
      {
        headerName: "",
        field: "isSelectedForMultiRegister",
        maxWidth: 52,
        sortable: false,
        filter: false,
        headerComponent: () => (
          <Center h="100%">
            <Checkbox
              aria-label="Velg alle register"
              checked={allActiveStatusBoardsSelected}
              disabled={activeStatusBoardIds.length === 0}
              indeterminate={someActiveStatusBoardsSelected && !allActiveStatusBoardsSelected}
              onClick={(e) => e.stopPropagation()}
              onChange={handleToggleAllActiveStatusBoards}
            />
          </Center>
        ),
        cellRenderer: (params: any) => {
          if (!params.data?._id) return null;
          return (
            <Center h="100%">
              <Checkbox
                aria-label="Velg register"
                checked={params.value === true}
                onClick={(e) => e.stopPropagation()}
                onChange={() => dispatch(toggleSelectedMultiRegisterIssueBoardIdInRedux(params.data._id.toString()))}
              />
            </Center>
          );
        },
      },
      {
        headerName: "",
        headerStyle: { paddingLeft: "11px" },
        field: "isFavorite",
        sortable: true,
        maxWidth: 60,
        // ↓ Lägg till detta
        sort: "desc",
        sortIndex: 0,
        cellRenderer: (params: any) => {
          if (!params.data?._id) return null;
          const isFavorite = params.value;
          return (
            <Tooltip openDelay={1000} label={isFavorite ? "Fjern fra favoritter" : "Legg til i favoritter"} withArrow>
              <button
                className="btn btn-link p-0 text-warning"
                onClick={handleToggleFavorite(params.data._id.toString())}
              >
                {isFavorite ? <IconStarFilled size={20} /> : <IconStar color="lightgrey" size={20} />}
              </button>
            </Tooltip>
          );
        },
      },
      {
        headerName: "Tittel",
        field: "name",
        sortable: true,
        filter: "agTextColumnFilter",
        flex: 3,
        minWidth: 250,
        // ↓ Lägg till detta
        sort: "asc",
        sortIndex: 1,
        cellRenderer: (params: any) => {
          if (!params.data) return null;
          return (
            <Anchor fw={500} onClick={handleTitleClick(params.data)}>
              {String(params.value ?? "").toUpperCase()}
            </Anchor>
          );
        },
      },
      {
        headerName: "Type register",
        field: "category",
        sortable: true,
        filter: "agSetColumnFilter",
        cellClass: "px-1",
        minWidth: 135,
      },
      {
        headerName: "Opprettet fra mal",
        field: "madeFromTemplate.title",
        sortable: true,
        filter: "agTextColumnFilter",
        cellClass: "px-1",
        minWidth: 135,
      },
      {
        headerName: "Tilgang",
        field: "access",
        sortable: true,
        filter: "agSetColumnFilter",
        cellClass: "px-1",
        minWidth: 145,
        valueFormatter: ({ value }) => generateAccessText(value, "sm"),
        cellRenderer: (params: any) => (
          <div>{params.value != null && <AccessView access={params.value} variant="sm" />}</div>
        ),
      },
      {
        headerName: "Status",
        field: "progress",
        cellRenderer: (params: any) => (
          <div className="w-100">
            {Array.isArray(params.value) && (
              <div className="progress thin-progress" style={{ height: "6px" }}>
                {params.value.map((progress: any, index: number) => (
                  <div
                    key={index}
                    className="progress-bar"
                    style={{
                      width: `${progress.percentage}%`,
                      backgroundColor: progress.color,
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        ),
        comparator: (valueA: IProgressData[], valueB: IProgressData[]) => {
          const isArrayA = Array.isArray(valueA);
          const isArrayB = Array.isArray(valueB);

          if (!isArrayA && !isArrayB) return 0;
          if (!isArrayA) return -1;
          if (!isArrayB) return 1;

          const vA = valueA[3]?.percentage || 0;
          const vB = valueB[3]?.percentage || 0;

          if (vA < vB) return -1;
          if (vA > vB) return 1;

          const sumA = valueA.reduce((acc, curr) => acc + (curr.percentage || 0), 0);
          const sumB = valueB.reduce((acc, curr) => acc + (curr.percentage || 0), 0);

          return sumA - sumB;
        },
      },
      {
        headerName: "Opprettet av",
        field: "createdBy.name",
        sortable: true,
        filter: "agSetColumnFilter",
        flex: 2,
      },
      {
        headerName: "Opprettet",
        field: "createdAt",
        sortable: true,
        filter: "agDateColumnFilter",
        cellRenderer: (params: any) => formatShortDate(params.value),
      },
      {
        headerName: "PNS",
        field: "pns",
        sortable: true,
        filter: "agSetColumnFilter",
        cellClass: "px-1",
        minWidth: 135,
        cellRenderer: (params: any) => {
          if (params.value != null) {
            const pnsItem = pnsItems.find((pns) => pns.props.pnsId === params.value);
            return (
              <div className="truncateHeaderText">
                {params.value} - {pnsItem?.props?.title || ""}
              </div>
            );
          }
          return "";
        },
      },
    ],
    [
      activeStatusBoardIds.length,
      allActiveStatusBoardsSelected,
      dispatch,
      handleTitleClick,
      handleToggleAllActiveStatusBoards,
      handleToggleFavorite,
      pnsItems,
      someActiveStatusBoardsSelected,
    ],
  );

  const onCellClicked = useCallback(
    (e) => {
      if (e.colDef.field === "isSelectedForMultiRegister") return;
      if (e.colDef.field === "isFavorite") return; // ignore clicks on the link cell
      e.api.deselectAll();
      goToBoard(e.data);
    },
    [goToBoard],
  );

  return (
    <Stack style={{ flex: 1 }} gap="xs">
      {filteredBoards != null && (
        <>
          <Group align="center" gap="xs" px="xs" wrap="nowrap">
            <CreateNewButton size="sm" onClick={addNew} />
            <Menu shadow="md" position="bottom-start">
              <Menu.Target>
                <Button
                  size="sm"
                  variant="outline"
                  color="mc-primary"
                  disabled={selectedMultiRegisterIssueBoardCount === 0}
                  rightSection={<IconChevronDown size={16} />}
                >
                  Valgte registre ({selectedMultiRegisterIssueBoardCount})
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item leftSection={<IconDatabaseImport size={16} />} onClick={readDataFromSelectedBoards}>
                  Les data fra valgte registre
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
            <SegmentedControl
              fullWidth
              value={activeStatus}
              onChange={(status) => dispatch(setIssueBoardsTableActiveStatusInRedux(status as IssueBoardStatusEnums))}
              color="mc-primary"
              size="md"
              data={ISSUE_BOARD_STATUSES.map((status: IValueLabel) => {
                const count = statusCounts[status.value] || 0;
                return {
                  value: status.value,
                  label: (
                    <Center inline style={{ gap: 10, whiteSpace: "nowrap" }} px="sm">
                      <span>{status.label}</span>
                      <Box bg="warmgray.5" fz="8px" style={{ scale: "1.3" }} className="circle-singleline">
                        {count}
                      </Box>
                    </Center>
                  ),
                };
              })}
            />
            <TextInput
              placeholder="Søk i tabell"
              onChange={(e) => setFilterText(e.target.value)}
              size="sm"
              w={160}
              leftSection={<IconFilter2Search size={16} />}
            />
            <IssueSearch projectId={project._id} />
            {isLoading === true && <Loader size={20} />}
          </Group>

          <Box className="checklistTable ag-theme-quartz" style={{ flex: 1, width: "100%" }} p={4}>
            <AgGridReact
              rowData={filteredBoards}
              defaultColDef={{
                suppressHeaderMenuButton: true,
                autoHeight: false,
                minWidth: 100,
                flex: 1,
                cellStyle: {
                  whiteSpace: "normal",
                  lineHeight: "1.5",
                  wordBreak: "break-word",
                },
              }}
              columnDefs={issueBoardColumnDefs}
              rowHeight={50}
              suppressClickEdit={true}
              rowSelection={{ mode: "singleRow", checkboxes: false }}
              animateRows={true}
              suppressContextMenu={true}
              localeText={AG_GRID_LOCALE_NOR}
              quickFilterText={filterText}
              getRowId={(params) => params.data._id} // Stabil rendering
              onCellClicked={onCellClicked}
            />
          </Box>
        </>
      )}
    </Stack>
  );
};

export default React.memo(IssueBoardsTable);
